import React, { useState, useEffect } from "react";

// Line-art gesture illustrations (scan frame + face + the hand doing the gesture),
// shown in the liveness gesture challenge instead of an emoji. Strokes use
// currentColor so they follow the theme.
const S = { fill: "none", stroke: "currentColor", strokeWidth: 3.2, strokeLinecap: "round", strokeLinejoin: "round" };
const F = { fill: "currentColor" };

function Frame() {
  return (
    <g style={S}>
      <path d="M10 22 V10 H22" />
      <path d="M54 10 H66 V22" />
      <path d="M66 54 V66 H54" />
      <path d="M22 66 H10 V54" />
    </g>
  );
}

// A simple head, optionally hiding one eye (when a hand covers it).
function Head({ leftEye = true, rightEye = true }) {
  return (
    <g style={S}>
      <path d="M24 26 a14 15 0 0 1 28 0 v10 a14 16 0 0 1 -28 0 z" />
      {leftEye && <line x1="31" y1="30" x2="31" y2="33" />}
      {rightEye && <line x1="45" y1="30" x2="45" y2="33" />}
      <path d="M35 37 q3 2 6 0" />
    </g>
  );
}

function VOverEye() {
  // Two fingers in a V over the left eye.
  return (
    <>
      <Frame />
      <Head leftEye={false} />
      <g style={S}>
        <line x1="27" y1="14" x2="30" y2="30" />
        <line x1="35" y1="14" x2="32" y2="30" />
        <path d="M29 30 q3 5 5 0" />
      </g>
    </>
  );
}

function OpenPalm() {
  // Open hand with five spread fingers, beside the face.
  return (
    <>
      <Frame />
      <Head />
      <g style={S} transform="translate(2 0)">
        <path d="M50 46 v-10" />
        <path d="M54 46 v-13" />
        <path d="M58 46 v-11" />
        <path d="M62 45 v-8" />
        <path d="M47 44 l-3 -5" />
        <path d="M46 46 h16 v6 a8 8 0 0 1 -16 0 z" />
      </g>
    </>
  );
}

function ThumbsUp() {
  // Fist with the thumb pointing up.
  return (
    <>
      <Frame />
      <Head />
      <g style={S}>
        <rect x="46" y="42" width="16" height="16" rx="4" />
        <path d="M50 42 v-6 a3 3 0 0 1 6 0 v6" />
        <line x1="50" y1="48" x2="58" y2="48" />
        <line x1="50" y1="52" x2="58" y2="52" />
      </g>
    </>
  );
}

function HandOnCheek() {
  // Open hand resting on the right cheek.
  return (
    <>
      <Frame />
      <Head />
      <g style={S}>
        <path d="M44 40 v-8" />
        <path d="M48 40 v-10" />
        <path d="M52 40 v-8" />
        <path d="M42 39 h12 v6 a6 6 0 0 1 -12 0 z" />
      </g>
    </>
  );
}

const MAP = {
  v_over_eye: VOverEye,
  open_palm: OpenPalm,
  thumbs_up: ThumbsUp,
  hand_on_cheek: HandOnCheek,
};

// `png` (default true): prefer a bundled illustration at public/gestures/<key>.png
// and fall back to the built-in line-art SVG if it's absent. Set png={false} to
// always render the theme-aware SVG (e.g. small inline use in the review).
export default function GestureIcon({ gesture, className, png = true }) {
  const [imgOk, setImgOk] = useState(png);
  useEffect(() => setImgOk(png), [png, gesture]);
  const Draw = MAP[gesture];
  if (png && imgOk && Draw) {
    const url = `${process.env.PUBLIC_URL || ""}/gestures/${gesture}.png`;
    return <img src={url} alt={gesture} className={className} onError={() => setImgOk(false)} />;
  }
  if (!Draw) return null;
  return (
    <svg viewBox="0 0 76 76" className={className} role="img" aria-label={gesture}>
      <Draw />
    </svg>
  );
}
